/* global BigInt */
// City Helpers — pure game logic.
//
// A daily social-emotional-learning game. Every day the child gets DAILY_COUNT
// "helper moments" chosen deterministically from a 26-scenario catalog, with
// DISTINCT city anchors, seeded by the local calendar day (+ optional profile).
// Nothing in this file touches React — it is all pure data + functions, so the
// daily selection and persistence can be unit-tested in isolation.

export const ASSET_BASE = "/assets/games/city-helpers";

/** Full public URL for one of the game's PNG sprites (name without extension). */
export const spriteUrl = (name) => `${ASSET_BASE}/${name}.png`;

export const DAILY_COUNT = 5;

// ---------------------------------------------------------------------------
// SEL skills — shown as friendly badges, never as jargon.
// ---------------------------------------------------------------------------

export const SEL_SKILLS = {
  kindness: { label: "Kindness" },
  empathy: { label: "Empathy" },
  responsibility: { label: "Responsibility" },
  friendship: { label: "Friendship" },
  calm: { label: "Calm Down Power" },
  gratitude: { label: "Gratitude" },
  patience: { label: "Patience" },
  feelings: { label: "Feelings" },
  honesty: { label: "Honesty" },
};

// ---------------------------------------------------------------------------
// City anchors — named spots on the wide painted plate. x/y are unit
// coordinates (0..1) across the scrollable cityscape, mirroring the iOS
// CityAnchor.unitPoint source of truth. Two scenarios may share an anchor in
// the POOL, but never on the same day (selection keeps them distinct), so
// markers never overlap.
// ---------------------------------------------------------------------------

export const CITY_ANCHORS = {
  playground: { x: 0.06, y: 0.68 },
  parkBench: { x: 0.2, y: 0.67 },
  pond: { x: 0.19, y: 0.68 },
  fountain: { x: 0.27, y: 0.67 },
  garden: { x: 0.35, y: 0.69 },
  crosswalk: { x: 0.41, y: 0.75 },
  busStop: { x: 0.45, y: 0.73 },
  streetCorner: { x: 0.5, y: 0.74 },
  bakery: { x: 0.55, y: 0.72 },
  iceCreamStand: { x: 0.63, y: 0.72 },
  school: { x: 0.6, y: 0.7 },
  fireStation: { x: 0.72, y: 0.72 },
  market: { x: 0.8, y: 0.73 },
  library: { x: 0.89, y: 0.72 },
};

// ---------------------------------------------------------------------------
// Backdrops behind a full-screen activity.
// ---------------------------------------------------------------------------

export const BACKDROPS = {
  park: "ch_bg_park",
  street: "ch_bg_street",
  calm: "ch_bg_calm",
};

// ---------------------------------------------------------------------------
// The catalog — every helper moment the city knows, faithfully ported from the
// iOS HelperScenarioCatalog. Each scenario is pure data built from one of the
// sixteen reusable interaction templates.
//
// A character is { asset, happyAsset? } — the sprite crossfades to happyAsset
// on success.
// ---------------------------------------------------------------------------

export const SCENARIOS = [
  // 1 — Wait for the light, then walk a neighbor across.
  {
    id: "crosswalk_help",
    title: "Cross Safely",
    skill: "responsibility",
    anchor: "crosswalk",
    iconAsset: "ch_footprints",
    introLine:
      "Mr. Lee is waiting to cross the busy street. Let's help him get across safely!",
    takeawayLine:
      "You waited for the green light and helped a neighbor. Looking out for others keeps everyone safe!",
    backdrop: "street",
    template: {
      type: "tapSequence",
      prompt: "Wait for the green light… then tap the glowing footsteps!",
      stepAsset: "ch_footprints",
      stepPoints: [
        { x: 0.18, y: 0.8 },
        { x: 0.4, y: 0.66 },
        { x: 0.6, y: 0.52 },
        { x: 0.78, y: 0.38 },
      ],
      goalPoint: { x: 0.85, y: 0.2 },
      goalLabel: "Mr. Lee made it across!",
      escort: { asset: "ch_mrlee_waiting", happyAsset: "ch_mrlee_happy" },
      waitForGreenLight: true,
    },
  },

  // 2 — Pick up litter and sort it into the right bins.
  {
    id: "litter_sort",
    title: "Tidy the Street",
    skill: "responsibility",
    anchor: "streetCorner",
    iconAsset: "ch_litter_banana",
    introLine:
      "Oh no, someone left litter on the sidewalk! Let's clean it up and sort it right.",
    takeawayLine:
      "You made the street sparkle! Taking care of our city is a helper superpower.",
    backdrop: "street",
    template: {
      type: "dragToTarget",
      prompt: "Drag each piece to the right bin!",
      items: [
        { id: "banana", asset: "ch_litter_banana", label: "Banana peel", targetId: "trash" },
        { id: "apple", asset: "ch_litter_apple", label: "Apple core", targetId: "trash" },
        { id: "paper", asset: "ch_litter_paper", label: "Paper ball", targetId: "recycle" },
        { id: "bottle", asset: "ch_litter_bottle", label: "Bottle", targetId: "recycle" },
      ],
      targets: [
        { id: "trash", asset: "ch_bin_trash", label: "Trash", tint: "rgb(115,128,148)", scale: 1.2 },
        { id: "recycle", asset: "ch_bin_recycle", label: "Recycle", tint: "rgb(51,179,107)", scale: 1.2 },
      ],
    },
  },

  // 3 — Notice a sad friend and choose a caring response.
  {
    id: "cheer_friend",
    title: "Cheer Up a Friend",
    skill: "empathy",
    anchor: "parkBench",
    iconAsset: "ch_maya_sad",
    introLine: "Maya is sitting alone on the bench, and she looks sad.",
    takeawayLine:
      "You noticed a friend's feelings and helped. That's empathy — it makes hearts feel better!",
    backdrop: "park",
    template: {
      type: "chooseKind",
      prompt: "What would a kind friend do?",
      character: { asset: "ch_maya_sad", happyAsset: "ch_maya_happy" },
      choices: [
        { id: "ask", text: "Ask her, “Are you okay?”", isKind: true },
        {
          id: "ignore",
          text: "Pretend you don't see her",
          isKind: false,
          nudge: "Hmm… Maya might really need a friend right now. What else could we try?",
        },
        { id: "share", text: "Sit with her and share your toy", isKind: true },
      ],
    },
  },

  // 4 — Big feelings: breathe first, then choose a kind fix.
  {
    id: "dropped_ice_cream",
    title: "The Big Oops",
    skill: "calm",
    anchor: "iceCreamStand",
    iconAsset: "ch_icecream",
    introLine: "Sam's ice cream just went SPLAT! Sam feels really, really upset.",
    takeawayLine:
      "Big feelings get smaller with slow breaths. You helped Sam feel calm AND cared for!",
    backdrop: "calm",
    template: {
      type: "breatheTrace",
      prompt: "Let's take slow balloon breaths with Sam to calm down.",
      breaths: 3,
      followUp: {
        type: "chooseKind",
        prompt: "Sam feels calmer now. What's a kind thing to do?",
        character: { asset: "ch_sam_calm" },
        choices: [
          { id: "share", text: "Offer to share your ice cream", isKind: true },
          {
            id: "dismiss",
            text: "Say “It's just ice cream!”",
            isKind: false,
            nudge: "Sam's feelings are real. What would feel kind if it happened to you?",
          },
          { id: "hug", text: "Say “That happens to me too — want a hug?”", isKind: true },
        ],
      },
    },
  },

  // 5 — Share your umbrella with a soggy friend (the rain stops!).
  {
    id: "share_umbrella",
    title: "Rainy Day Rescue",
    skill: "kindness",
    anchor: "busStop",
    iconAsset: "ch_umbrella",
    introLine:
      "It's raining and Theo doesn't have an umbrella. Brrr — he's getting soaked!",
    takeawayLine:
      "Sharing what you have is one of the kindest things ever. Theo is warm and dry now!",
    backdrop: "street",
    template: {
      type: "dragToTarget",
      prompt: "Drag the umbrella over to Theo!",
      items: [{ id: "umbrella", asset: "ch_umbrella", label: "Umbrella", targetId: "theo" }],
      targets: [
        {
          id: "theo",
          asset: "ch_theo_rain",
          filledAsset: "ch_theo_happy",
          label: "Theo",
          tint: "rgb(64,140,242)",
          scale: 2.1,
        },
      ],
      effect: "rain",
    },
  },

  // 6 — Follow the paw prints and reunite a lost puppy.
  {
    id: "lost_puppy",
    title: "The Lost Puppy",
    skill: "friendship",
    anchor: "pond",
    iconAsset: "ch_pawprint",
    introLine:
      "A little puppy wandered away from home! Follow the paw prints to find her.",
    takeawayLine:
      "You paid attention and helped someone little who was lost. What a hero!",
    backdrop: "park",
    template: {
      type: "tapSequence",
      prompt: "Tap the glowing paw prints, one by one!",
      stepAsset: "ch_pawprint",
      stepPoints: [
        { x: 0.16, y: 0.28 },
        { x: 0.4, y: 0.44 },
        { x: 0.28, y: 0.64 },
        { x: 0.55, y: 0.76 },
        { x: 0.74, y: 0.58 },
      ],
      goalPoint: { x: 0.84, y: 0.34 },
      goalLabel: "You found Biscuit!",
      goalCharacter: { asset: "ch_puppy_sad", happyAsset: "ch_puppy_happy" },
    },
  },

  // 7 — Water the thirsty community garden.
  {
    id: "water_garden",
    title: "Thirsty Garden",
    skill: "responsibility",
    anchor: "garden",
    iconAsset: "ch_flower_bloom",
    introLine: "The community garden is so thirsty — the flowers are drooping!",
    takeawayLine: "Living things grow when we care for them. The garden says thank you!",
    backdrop: "park",
    template: {
      type: "waterGarden",
      prompt: "Hold the watering can over each thirsty flower!",
      flowers: [
        { wilted: "ch_flower_wilted", bloom: "ch_flower_bloom", tint: "rgb(242,115,153)" },
        { wilted: "ch_flower_wilted_yellow", bloom: "ch_flower_bloom_yellow", tint: "rgb(255,184,51)" },
        { wilted: "ch_flower_wilted_purple", bloom: "ch_flower_bloom_purple", tint: "rgb(184,115,242)" },
        { wilted: "ch_flower_wilted_orange", bloom: "ch_flower_bloom_orange", tint: "rgb(255,140,77)" },
      ],
    },
  },

  // 8 — Include the new kid at the playground.
  {
    id: "welcome_new_kid",
    title: "The New Kid",
    skill: "friendship",
    anchor: "playground",
    iconAsset: "ch_newkid_shy",
    introLine: "There's a new kid at the playground. He doesn't know anyone yet.",
    takeawayLine:
      "Including someone new turns a stranger into a friend. You made the playground kinder!",
    backdrop: "park",
    template: {
      type: "chooseKind",
      prompt: "What would help him feel welcome?",
      character: { asset: "ch_newkid_shy", happyAsset: "ch_newkid_happy" },
      choices: [
        { id: "invite", text: "Say “Hi! Want to play with us?”", isKind: true },
        {
          id: "whisper",
          text: "Whisper with your friends instead",
          isKind: false,
          nudge: "Whispering might make him feel left out. How can we include him?",
        },
        { id: "tour", text: "Show him your favorite slide", isKind: true },
      ],
    },
  },

  // 9 — Help Grandma gather her spilled groceries.
  {
    id: "grandma_groceries",
    title: "Grandma's Groceries",
    skill: "kindness",
    anchor: "bakery",
    iconAsset: "ch_basket",
    introLine: "Oh no — Grandma Rosa's grocery bag ripped and everything rolled away!",
    takeawayLine: "You jumped in to help without being asked. Helpers make the whole city smile!",
    backdrop: "street",
    template: {
      type: "dragToTarget",
      prompt: "Drag the groceries back into her basket!",
      items: [
        { id: "apple", asset: "ch_grocery_apple", label: "Apple", targetId: "basket" },
        { id: "bread", asset: "ch_grocery_bread", label: "Bread", targetId: "basket" },
        { id: "milk", asset: "ch_grocery_milk", label: "Milk", targetId: "basket" },
      ],
      targets: [
        { id: "basket", asset: "ch_basket", label: "Grandma's basket", tint: "rgb(204,133,64)", scale: 1.4 },
      ],
      sideCharacter: { asset: "ch_grandma_worried", happyAsset: "ch_grandma_happy" },
    },
  },

  // 10 — Name the feelings: match each face to its feeling.
  {
    id: "feelings_checkin",
    title: "Feelings Fountain",
    skill: "feelings",
    anchor: "fountain",
    iconAsset: "ch_face_happy",
    introLine:
      "The kids at the fountain are showing their feelings. Can you match each face?",
    takeawayLine:
      "Naming feelings helps us understand each other. And remember — all feelings are okay!",
    backdrop: "calm",
    template: {
      type: "feelingsMatch",
      prompt: "Which face is feeling this? Tap it!",
      feelings: [
        { name: "Happy", faceAsset: "ch_face_happy", poolAsset: "ch_zone_sun", tint: "rgb(255,184,51)" },
        { name: "Sad", faceAsset: "ch_face_sad", poolAsset: "ch_zone_rain", tint: "rgb(77,140,230)" },
        { name: "Angry", faceAsset: "ch_face_angry", poolAsset: "ch_zone_volcano", tint: "rgb(230,89,77)" },
      ],
    },
  },

  // 11 — Say thank you to a community helper.
  {
    id: "thank_bus_driver",
    title: "Thank You, Driver!",
    skill: "gratitude",
    anchor: "busStop",
    iconAsset: "ch_penny_bus",
    introLine: "Miss Penny drives the bus all day so everyone gets home safe.",
    takeawayLine:
      "A little “thank you” is tiny to say but HUGE to hear. You made Miss Penny's day!",
    backdrop: "street",
    template: {
      type: "cheerMeter",
      prompt: "Wave and cheer to say thank you, Miss Penny!",
      character: { asset: "ch_penny_bus" },
      style: "wave",
      taps: 10,
      cheerWord: "Thanks!",
    },
  },

  // 12 — Waiting is hard: wait calmly while the line moves.
  {
    id: "wait_your_turn",
    title: "Waiting Is Hard",
    skill: "patience",
    anchor: "iceCreamStand",
    iconAsset: "ch_icecream",
    introLine: "The ice cream line is soooo long. Waiting can feel wiggly!",
    takeawayLine:
      "You waited patiently for your turn — and the wait was worth it. Scoop, there it is!",
    backdrop: "calm",
    template: {
      type: "waitTurn",
      prompt: "Wait calmly… tap ONLY when it's your turn!",
      kidsAhead: ["ch_newkid_happy", "ch_sam_calm", "ch_maya_happy"],
      standAsset: "ch_icecream",
      treatAsset: "ch_icecream",
    },
  },

  // 13 — Share with a classmate who forgot their snack.
  {
    id: "forgotten_snack",
    title: "The Forgotten Snack",
    skill: "kindness",
    anchor: "school",
    iconAsset: "ch_cookie_whole",
    introLine: "Oh no — Ellie forgot her snack at home, and it's snack time at school!",
    takeawayLine: "Sharing turns one snack into two smiles. Ellie can't stop grinning!",
    backdrop: "street",
    template: {
      type: "splitShare",
      prompt: "Tap your cookie to break it — then share half with Ellie!",
      wholeAsset: "ch_cookie_whole",
      halfAsset: "ch_cookie_half",
      friend: { asset: "ch_ellie_hungry", happyAsset: "ch_ellie_happy" },
    },
  },

  // 14 — Loud sounds feel smaller after slow breaths.
  {
    id: "loud_alarm",
    title: "The Loud Alarm",
    skill: "calm",
    anchor: "fireStation",
    iconAsset: "ch_bell",
    introLine:
      "CLANG CLANG! The fire station's practice bell is SO loud, it made everyone jump!",
    takeawayLine:
      "Loud noises can startle us — and slow, gentle hands can turn the noise right down.",
    backdrop: "calm",
    template: {
      type: "calmAlarm",
      prompt: "Slide the shhh-cloud down slowly to quiet the loud bell.",
      hushes: 3,
      bellAsset: "ch_bell",
    },
  },

  // 15 — The honest thing, even when nobody saw.
  {
    id: "extra_apple",
    title: "The Extra Apple",
    skill: "honesty",
    anchor: "market",
    iconAsset: "ch_grocery_apple",
    introLine:
      "The market seller gave you an extra apple by mistake. Nobody saw… except you!",
    takeawayLine:
      "Being honest feels better than any extra apple. The seller was SO proud of you!",
    backdrop: "street",
    template: {
      type: "honestReturn",
      prompt: "One apple is extra — give it back to the seller's crate!",
      itemAsset: "ch_grocery_apple",
      basketAsset: "ch_crate_apple",
      yourCount: 3,
    },
  },

  // 16 — Quiet kindness in a shared place.
  {
    id: "library_whisper",
    title: "Quiet as a Mouse",
    skill: "friendship",
    anchor: "library",
    iconAsset: "ch_footprints",
    introLine: "Inside the library, friends are deep in their storybooks. Shhh…",
    takeawayLine:
      "Quiet kindness lets everyone enjoy their stories. The readers wave a silent thank-you!",
    backdrop: "calm",
    template: {
      type: "tiptoe",
      prompt: "Tiptoe slowly across — tap each footstep gently, not too fast!",
      stepAsset: "ch_footprints",
      readers: ["ch_maya_happy", "ch_newkid_happy"],
      steps: 5,
    },
  },

  // 17 — Rake the windy mess off Mr. Lee's favorite bench.
  {
    id: "falling_leaves",
    title: "Leaf Pile Party",
    skill: "responsibility",
    anchor: "parkBench",
    iconAsset: "ch_leaf",
    introLine: "Whoosh! The autumn wind covered Mr. Lee's favorite bench in leaves!",
    takeawayLine:
      "You turned a messy bench into a tidy park — and leaf piles are the best part of fall!",
    backdrop: "park",
    template: {
      type: "leafPile",
      prompt: "Tap the leaves to sweep them into a pile!",
      leafCount: 7,
      sideCharacter: { asset: "ch_mrlee_waiting", happyAsset: "ch_mrlee_happy" },
    },
  },

  // 18 — Guide a wobbly duckling home across the lily pads.
  {
    id: "wobbly_duckling",
    title: "The Wobbly Duckling",
    skill: "empathy",
    anchor: "pond",
    iconAsset: "ch_duckling_sad",
    introLine:
      "Peep peep! A little duckling wobbled away from the pond and can't find her way back.",
    takeawayLine:
      "You noticed someone small who needed help — and now she's splashing happily at home!",
    backdrop: "park",
    template: {
      type: "tapSequence",
      prompt: "Tap the lily pads to show her the way home!",
      stepAsset: "ch_lilypad",
      stepPoints: [
        { x: 0.18, y: 0.3 },
        { x: 0.42, y: 0.44 },
        { x: 0.3, y: 0.64 },
        { x: 0.58, y: 0.74 },
      ],
      goalPoint: { x: 0.8, y: 0.52 },
      goalLabel: "Splash — home at last!",
      escort: { asset: "ch_duckling_sad", happyAsset: "ch_duckling_happy" },
    },
  },

  // 19 — Return the runaway balloon to its tiny owner.
  {
    id: "balloon_rescue",
    title: "Balloon Rescue",
    skill: "kindness",
    anchor: "playground",
    iconAsset: "ch_balloon_red",
    introLine: "A red balloon drifted away… and its little owner is crying big tears.",
    takeawayLine: "You gave back something precious. Look at that happy dance!",
    backdrop: "park",
    template: {
      type: "dragToTarget",
      prompt: "Drag the balloon back to its friend!",
      items: [{ id: "balloon", asset: "ch_balloon_red", label: "Red balloon", targetId: "kid" }],
      targets: [
        {
          id: "kid",
          asset: "ch_kid_crying",
          filledAsset: "ch_kid_joyful",
          label: "Little friend",
          tint: "rgb(242,115,102)",
          scale: 2.1,
        },
      ],
    },
  },

  // 20 — Stay statue-still while a fuzzy visitor says hello.
  {
    id: "bee_kind",
    title: "Bee Calm",
    skill: "calm",
    anchor: "garden",
    iconAsset: "ch_bee",
    introLine: "Bzzzz! A fuzzy bee is visiting the garden, and it feels a little scary.",
    takeawayLine:
      "You stayed calm and gentle, and the bee buzzed happily back to her flowers. Being still is a superpower!",
    backdrop: "calm",
    template: {
      type: "beeStill",
      prompt: "Be a statue! Don't touch while the bee flutters over to say hello.",
      visits: 2,
      companionAsset: "ch_bee",
    },
  },

  // 21 — Heroes walk, never run.
  {
    id: "fire_drill",
    title: "Fire Drill Heroes",
    skill: "responsibility",
    anchor: "fireStation",
    iconAsset: "ch_footprints",
    introLine:
      "Practice time at the fire station! Heroes walk — never run — to the meeting spot.",
    takeawayLine:
      "You practiced staying calm and walking safely. That's exactly what real heroes do!",
    backdrop: "street",
    template: {
      type: "tapSequence",
      prompt: "Tap the footsteps: nice and steady, one at a time.",
      stepAsset: "ch_footprints",
      stepPoints: [
        { x: 0.2, y: 0.75 },
        { x: 0.42, y: 0.62 },
        { x: 0.34, y: 0.42 },
        { x: 0.58, y: 0.3 },
      ],
      goalPoint: { x: 0.8, y: 0.22 },
      goalLabel: "Safe and sound!",
      escort: { asset: "ch_sam_calm" },
    },
  },

  // 22 — Sort the toppled fruit back into its crates.
  {
    id: "market_mixup",
    title: "Market Mix-Up",
    skill: "responsibility",
    anchor: "market",
    iconAsset: "ch_orange",
    introLine: "Crash! The fruit crates tipped over and everything rolled together.",
    takeawayLine: "Sorted and sparkling! The seller says every helper deserves a shiny apple.",
    backdrop: "street",
    template: {
      type: "dragToTarget",
      prompt: "Sort the fruit back into the right crates!",
      items: [
        { id: "apple1", asset: "ch_grocery_apple", label: "Apple", targetId: "apples" },
        { id: "apple2", asset: "ch_grocery_apple", label: "Apple", targetId: "apples" },
        { id: "orange1", asset: "ch_orange", label: "Orange", targetId: "oranges" },
        { id: "orange2", asset: "ch_orange", label: "Orange", targetId: "oranges" },
      ],
      targets: [
        { id: "apples", asset: "ch_crate_apple", label: "Apples", tint: "rgb(230,77,71)", scale: 1.15 },
        { id: "oranges", asset: "ch_crate_orange", label: "Oranges", tint: "rgb(255,158,41)", scale: 1.15 },
      ],
    },
  },

  // 23 — Get every story ready for a new friend.
  {
    id: "library_books",
    title: "Story Time Delivery",
    skill: "kindness",
    anchor: "library",
    iconAsset: "ch_book",
    introLine: "Story time starts soon, but the returned books are still in a pile!",
    takeawayLine:
      "Now every story is ready for a new friend to love. Readers everywhere say thank you!",
    backdrop: "calm",
    template: {
      type: "shelveBooks",
      prompt: "Put each book on the shelf spot that matches its color!",
      shelfAsset: "ch_bookshelf",
      books: [
        { id: "red", asset: "ch_book_red", tint: "rgb(230,77,82)" },
        { id: "blue", asset: "ch_book_blue", tint: "rgb(71,140,242)" },
        { id: "green", asset: "ch_book_green", tint: "rgb(92,184,102)" },
        { id: "purple", asset: "ch_book_purple", tint: "rgb(158,107,230)" },
      ],
    },
  },

  // 24 — Tiptoe-quiet while a tiny friend naps.
  {
    id: "sleepy_kitten",
    title: "The Sleepy Kitten",
    skill: "patience",
    anchor: "bakery",
    iconAsset: "ch_kitten",
    introLine: "Shhh… a tiny kitten fell asleep right by the bakery door.",
    takeawayLine:
      "You waited so gently that the kitten purred in her sleep. Patience is very, very kind.",
    backdrop: "calm",
    template: {
      type: "petSoothe",
      prompt: "Gently stroke the kitten back and forth to help her nap.",
      companionAsset: "ch_kitten",
      strokes: 6,
    },
  },

  // 25 — Cheer a friend's brand-new trick.
  {
    id: "compliment_friend",
    title: "The Whistle Wow",
    skill: "friendship",
    anchor: "fountain",
    iconAsset: "ch_face_happy",
    introLine: "Maya finally learned how to whistle — tweeeet! She's so excited to show you.",
    takeawayLine: "Cheering for friends makes their happy even bigger — and yours too!",
    backdrop: "park",
    template: {
      type: "cheerMeter",
      prompt: "Tap fast to cheer for Maya's brand-new whistle!",
      character: { asset: "ch_maya_happy" },
      style: "cheer",
      taps: 12,
      cheerWord: "Wow!",
    },
  },

  // 26 — Share the heavy load.
  {
    id: "heavy_bags",
    title: "The Heavy Bags",
    skill: "kindness",
    anchor: "busStop",
    iconAsset: "ch_bag",
    introLine: "Mr. Lee's shopping bags are sooo heavy today. His arms are tired!",
    takeawayLine: "Strong helper! Sharing the heavy stuff makes everything lighter.",
    backdrop: "street",
    template: {
      type: "carry",
      prompt: "The bags are heavy — carry them slowly to the bench!",
      bagAsset: "ch_bag",
      benchAsset: "ch_bench",
      sideCharacter: { asset: "ch_mrlee_waiting", happyAsset: "ch_mrlee_happy" },
      bags: 2,
    },
  },
];

const SCENARIO_BY_ID = Object.fromEntries(SCENARIOS.map((s) => [s.id, s]));

export const scenarioById = (id) => SCENARIO_BY_ID[id] || null;

/**
 * Who/what stays behind in the city once a moment is done — the friend you
 * helped, living happily at their spot. null = the spot just glows clean.
 */
export const cityCelebrationAsset = (id) => {
  switch (id) {
    case "crosswalk_help": return "ch_mrlee_happy";
    case "litter_sort": return null;
    case "cheer_friend": return "ch_maya_happy";
    case "dropped_ice_cream": return "ch_sam_calm";
    case "share_umbrella": return "ch_theo_happy";
    case "lost_puppy": return "ch_puppy_happy";
    case "water_garden": return "ch_flower_bloom";
    case "welcome_new_kid": return "ch_newkid_happy";
    case "grandma_groceries": return "ch_grandma_happy";
    case "feelings_checkin": return "ch_face_happy";
    case "thank_bus_driver": return "ch_penny_bus";
    case "wait_your_turn": return "ch_icecream";
    case "forgotten_snack": return "ch_ellie_happy";
    case "loud_alarm": return "ch_bell";
    case "extra_apple": return "ch_grocery_apple";
    case "library_whisper": return "ch_maya_happy";
    case "falling_leaves": return "ch_mrlee_happy";
    case "wobbly_duckling": return "ch_duckling_happy";
    case "balloon_rescue": return "ch_kid_joyful";
    case "bee_kind": return "ch_bee";
    case "fire_drill": return "ch_sam_calm";
    case "market_mixup": return "ch_orange";
    case "library_books": return "ch_book";
    case "sleepy_kitten": return "ch_kitten";
    case "compliment_friend": return "ch_maya_happy";
    case "heavy_bags": return "ch_mrlee_happy";
    default: return null;
  }
};

/**
 * The character a moment is "about" — staged large on the intro card and
 * celebrated on the completion beat. null for object-only moments. Mirrors the
 * iOS HelperScenario.featuredCharacter extension.
 */
export const featuredCharacter = (scenario) => {
  const t = scenario.template;
  switch (t.type) {
    case "chooseKind":
      return t.character || null;
    case "tapSequence":
      return t.escort || t.goalCharacter || null;
    case "dragToTarget": {
      if (t.sideCharacter) return t.sideCharacter;
      if (t.targets.length === 1 && (t.targets[0].scale || 1) > 1.5) {
        return { asset: t.targets[0].asset, happyAsset: t.targets[0].filledAsset };
      }
      return null;
    }
    case "cheerMeter":
      return t.character || null;
    case "leafPile":
    case "carry":
      return t.sideCharacter || null;
    case "splitShare":
      return t.friend || null;
    default:
      // breatheTrace, waterGarden, feelingsMatch, petSoothe, shelveBooks,
      // calmAlarm, beeStill, waitTurn, honestReturn, tiptoe — no single
      // featured pose.
      return null;
  }
};

// ---------------------------------------------------------------------------
// Seedable RNG — SplitMix64 over an FNV-1a hash of "dateKey|profile". Stable
// across launches and platforms, plenty random for shuffling the catalog.
// Implemented on BigInt so the 64-bit arithmetic matches the iOS version.
// ---------------------------------------------------------------------------

const U64 = (n) => BigInt.asUintN(64, n);

export const fnv1a = (str) => {
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < str.length; i += 1) {
    h = U64(h ^ BigInt(str.charCodeAt(i) & 0xff));
    h = U64(h * 0x00000100000001b3n);
  }
  return h;
};

/** Returns a generator producing successive UInt64 BigInts. */
export const splitMix64 = (seed) => {
  let state = U64(seed);
  return () => {
    state = U64(state + 0x9e3779b97f4a7c15n);
    let z = state;
    z = U64((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n);
    z = U64((z ^ (z >> 27n)) * 0x94d049bb133111ebn);
    return U64(z ^ (z >> 31n));
  };
};

/** In-place-safe Fisher-Yates shuffle driven by a UInt64 generator. */
export const seededShuffle = (items, nextU64) => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Number(nextU64() % BigInt(i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// The wide plate is ≈3.4 screens across; markers are ~74px, so this unit gap
// always leaves a comfortable margin between any two of them.
const MIN_ANCHOR_GAP = 0.055;
const MIN_PER_SIDE = 2;
const HALF_LINE = 0.45;

const anchorX = (scenario) => CITY_ANCHORS[scenario.anchor]?.x ?? 0.5;
const isRight = (scenario) => anchorX(scenario) >= HALF_LINE;

/**
 * Deterministic daily pick: seeded shuffle of the whole catalog, then walk it
 * with the same guarantees as iOS —
 *  - NO OVERLAP: picked anchors are kept at least MIN_ANCHOR_GAP apart so their
 *    (large) markers can never sit on top of one another.
 *  - SPREAD: at least two missions in each half of the wide city.
 *  - FRESHNESS: scenarios in `avoiding` (yesterday's set) are used only as a
 *    last resort, so back-to-back days never feel the same.
 * Returns an array of scenario ids.
 */
export const seededScenarioIDs = (dateKeyStr, profileKey = "default", avoiding = []) => {
  const next = splitMix64(fnv1a(`${dateKeyStr}|${profileKey}`));
  const shuffled = seededShuffle(SCENARIOS, next);
  const avoid = new Set(avoiding);
  const picked = [];
  const usedAnchors = new Set();

  const rightCount = () => picked.filter(isRight).length;
  const leftCount = () => picked.length - rightCount();
  const farEnough = (scenario) => {
    const x = anchorX(scenario);
    return !picked.some((p) => Math.abs(anchorX(p) - x) < MIN_ANCHOR_GAP);
  };

  const pick = (matches, needed, freshOnly, respectGap = true) => {
    for (const scenario of shuffled) {
      if (picked.length >= DAILY_COUNT || !needed()) return;
      if (!matches(scenario)) continue;
      if (usedAnchors.has(scenario.anchor)) continue;
      if (picked.some((p) => p.id === scenario.id)) continue;
      if (freshOnly && avoid.has(scenario.id)) continue;
      if (respectGap && !farEnough(scenario)) continue;
      usedAnchors.add(scenario.anchor);
      picked.push(scenario);
    }
  };

  // Spread guarantee first (fresh, then any), then fill the rest — always
  // keeping markers a safe distance apart.
  pick(isRight, () => rightCount() < MIN_PER_SIDE, true);
  pick(isRight, () => rightCount() < MIN_PER_SIDE, false);
  pick((s) => !isRight(s), () => leftCount() < MIN_PER_SIDE, true);
  pick((s) => !isRight(s), () => leftCount() < MIN_PER_SIDE, false);
  pick(() => true, () => true, true);
  pick(() => true, () => true, false);
  // Last resort only (should never be needed): fill to DAILY_COUNT ignoring the
  // gap so the day is never short a mission.
  pick(() => true, () => true, false, false);
  return picked.map((s) => s.id);
};

/** True when a set of ids is clustered in one half or has two markers too close. */
const isClustered = (ids) => {
  const xs = ids
    .map((id) => scenarioById(id))
    .filter(Boolean)
    .map((s) => anchorX(s))
    .sort((a, b) => a - b);
  if (xs.length < DAILY_COUNT) return false;
  const rightSide = xs.filter((x) => x >= HALF_LINE).length;
  const leftSide = xs.length - rightSide;
  const tooClose = xs.some((x, i) => i > 0 && x - xs[i - 1] < MIN_ANCHOR_GAP);
  return rightSide < MIN_PER_SIDE || leftSide < MIN_PER_SIDE || tooClose;
};

/** Resolves ids into full scenario objects, dropping any the catalog forgot. */
export const scenariosForIDs = (ids) => ids.map(scenarioById).filter(Boolean);

// ---------------------------------------------------------------------------
// Daily persistence — the day's chosen set + completed ids, keyed by local day
// (and profile). Rolls over automatically when the calendar day changes.
// ---------------------------------------------------------------------------

export const STORAGE_PREFIX = "pixley_city_helpers_day_";

/** Stable local yyyy-MM-dd key from a Date. */
export const dateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const storageKey = (profileKey) => `${STORAGE_PREFIX}${profileKey || "default"}`;

const readStored = (profileKey) => {
  try {
    const raw = localStorage.getItem(storageKey(profileKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const refill = (ids, today, profileKey) => {
  const result = [...ids];
  const usedAnchors = new Set(ids.map((id) => scenarioById(id)?.anchor).filter(Boolean));
  for (const id of seededScenarioIDs(today, profileKey)) {
    if (result.length >= DAILY_COUNT) break;
    const s = scenarioById(id);
    if (!s || result.includes(id) || usedAnchors.has(s.anchor)) continue;
    usedAnchors.add(s.anchor);
    result.push(id);
  }
  return result;
};

/**
 * Today's day-state, restored if we already seeded this local day for this
 * profile, freshly seeded otherwise (actively avoiding the previous set).
 * Shape: { dateKey, scenarioIDs:[], completedIDs:[], starRequested:bool }.
 */
export const loadDayState = (profileKey = "default", now = new Date()) => {
  const today = dateKey(now);
  const stored = readStored(profileKey);

  if (stored && stored.dateKey === today) {
    // Drop ids the catalog no longer knows, top back up to a full set.
    const known = (stored.scenarioIDs || []).filter(scenarioById);
    let scenarioIDs = known;
    let completedIDs = (stored.completedIDs || []).filter((id) => known.includes(id));
    let changed = false;

    if (known.length < (stored.scenarioIDs || []).length || known.length < DAILY_COUNT) {
      scenarioIDs = refill(known, today, profileKey);
      completedIDs = (stored.completedIDs || []).filter((id) => scenarioIDs.includes(id));
      changed = true;
    }

    // A set seeded before the spread/no-overlap rules can be clustered in one
    // half OR have two markers too close together. If the day is still
    // untouched, reseed it so today already looks right.
    if (completedIDs.length === 0 && isClustered(scenarioIDs)) {
      scenarioIDs = seededScenarioIDs(today, profileKey);
      completedIDs = [];
      changed = true;
    }

    const state = {
      dateKey: today,
      scenarioIDs,
      completedIDs,
      starRequested: !!stored.starRequested,
    };
    if (changed) saveDayState(state, profileKey);
    return state;
  }

  const previous = stored ? stored.scenarioIDs || [] : [];
  const fresh = {
    dateKey: today,
    scenarioIDs: seededScenarioIDs(today, profileKey, previous),
    completedIDs: [],
    starRequested: false,
  };
  saveDayState(fresh, profileKey);
  return fresh;
};

export const saveDayState = (state, profileKey = "default") => {
  try {
    localStorage.setItem(storageKey(profileKey), JSON.stringify(state));
  } catch {
    // The game still works when storage is unavailable.
  }
  return state;
};
